/**
 * Utility function to safely get sport value for tournament management module
 * 
 * Priority order:
 * 1. tournament.sports (if tournament is loaded)
 * 2. location.state.selectedTournament.sports
 * 3. URL search params (?sport=)
 * 
 * Throws error if sport cannot be determined (no default to prevent incorrect API calls)
 * 
 * @param tournament - Tournament object (optional, if loaded)
 * @param location - React Router location object (optional, for location state and URL params)
 * @returns Sport value (e.g., 'Football', 'Badminton', 'Tennis')
 * @throws Error if sport cannot be determined
 */
export function getSportValue(
  tournament?: { sports?: string } | null,
  location?: { state?: any; search?: string }
): string {
  // Priority 1: If tournament is loaded, always use its sports property
  if (tournament?.sports) {
    return tournament.sports;
  }
  
  // Priority 2: Try location state (selectedTournament)
  if (location?.state?.selectedTournament?.sports) {
    return location.state.selectedTournament.sports;
  }
  
  // Priority 3: Try URL search params
  if (location?.search) {
    const urlParams = new URLSearchParams(location.search);
    const sportFromUrl = urlParams.get('sport');
    if (sportFromUrl) {
      return sportFromUrl;
    }
  }
  
  // If none available, throw error instead of defaulting to 'Football'
  // This prevents incorrect API calls with wrong sport parameter
  throw new Error(
    'Sport parameter is required. Please navigate from tournament list or include ?sport=Football in URL.'
  );
}

/**
 * Helper function for components that have tournament and location in scope
 * This is a convenience wrapper that uses React hooks context
 */
export function createGetSportValue(
  tournament: { sports?: string } | null | undefined,
  location: { state?: any; search?: string }
): () => string {
  return () => getSportValue(tournament, location);
}
